import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import {
  Banner,
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  PageHead,
  SkeletonList,
  StatusPill,
  useToast,
} from "@ui/react";
import { ChevronLeftIcon, PlusIcon, TrashIcon } from "@ui/react/icons";
import { deleteExtra, listExtras, listServices, updateExtra } from "../../lib/api/contractor";
import type { ContractorService, ServiceExtra } from "../../lib/types/models";
import { duration, money } from "../../lib/format";
import { errorMessage } from "../../lib/errorMessage";
import { routerLink } from "../../lib/routerLink";

export function ExtrasListScreen() {
  const toast = useToast();
  const [state, setState] = useState<{
    loading: boolean;
    error: unknown;
    extras: ServiceExtra[] | null;
    services: ContractorService[];
  }>({ loading: true, error: null, extras: null, services: [] });
  const [reloadKey, setReloadKey] = useState(0);
  const [pendingDelete, setPendingDelete] = useState<ServiceExtra | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  useEffect(() => {
    let ignore = false;
    setState((s) => ({ ...s, loading: true, error: null }));

    // Services come along so the "applies to" summary can name them; an extra
    // stores ids only, and a list of raw uuids tells a contractor nothing.
    void Promise.allSettled([listExtras(), listServices()]).then(([extras, services]) => {
      if (ignore) return;
      if (extras.status === "rejected") {
        setState({ loading: false, error: extras.reason, extras: null, services: [] });
        return;
      }
      setState({
        loading: false,
        error: null,
        extras: extras.value,
        services: services.status === "fulfilled" ? services.value : [],
      });
    });

    return () => {
      ignore = true;
    };
  }, [reloadKey]);

  const reload = useCallback(() => setReloadKey((key) => key + 1), []);
  const closeDialog = useCallback(() => setPendingDelete(null), []);

  const onDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    setActionError(null);
    try {
      // 204, no body.
      await deleteExtra(pendingDelete.extraId);
      setPendingDelete(null);
      toast.show("Add-on deleted.");
      reload();
    } catch (error) {
      setActionError(errorMessage(error));
      setPendingDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  const toggle = async (extra: ServiceExtra) => {
    setTogglingId(extra.extraId);
    try {
      await updateExtra(extra.extraId, {
        label: extra.label,
        description: extra.description ?? "",
        durationMinutes: extra.durationMinutes,
        priceAmount: extra.priceAmount,
        priceCurrency: extra.priceCurrency,
        applicableServiceIds: extra.applicableServiceIds ?? [],
        enabled: !extra.enabled,
      });
      toast.show(extra.enabled ? "Add-on disabled." : "Add-on enabled.");
      await reload();
    } catch (error) {
      setActionError(errorMessage(error));
    } finally {
      setTogglingId(null);
    }
  };

  const appliesTo = (extra: ServiceExtra): string => {
    const ids = extra.applicableServiceIds ?? [];
    if (ids.length === 0) return "Offered on every service";
    const named = ids
      .map((id) => state.services.find((service) => service.serviceId === id)?.name)
      .filter((serviceName): serviceName is string => Boolean(serviceName));
    if (named.length === 0) return `Offered on ${ids.length} ${ids.length === 1 ? "service" : "services"}`;
    return `Offered on ${named.join(", ")}`;
  };

  // One "add" affordance per state: the page head keeps quiet while the empty
  // state is carrying the invitation. See ServicesListScreen for the same rule.
  const populated = Boolean(state.extras && state.extras.length > 0);

  return (
    <div className="stack stack--loose">
      {/* No eyebrow: the back link below names this same section, and a head
          that says "Booking setup" twice in adjacent lines is stammering. See
          PageHead for the rule. */}
      <PageHead
        title="Add-ons"
        back={
          <Link className="back-link" to="/settings/booking">
            <ChevronLeftIcon />
            Booking setup
          </Link>
        }
        actions={
          populated ? (
            <Link className="btn btn-primary" to="/settings/extras/new">
              <PlusIcon />
              Add an add-on
            </Link>
          ) : undefined
        }
      />

      {actionError ? (
        <Banner tone="danger" title="That didn't work">
          {actionError}
        </Banner>
      ) : null}

      {state.loading ? (
        <div role="status">
          <span className="sr-only">Loading your add-ons</span>
          <SkeletonList rows={3} height="7rem" />
        </div>
      ) : state.error ? (
        <ErrorState message={errorMessage(state.error)} onRetry={reload} />
      ) : !state.extras || state.extras.length === 0 ? (
        <EmptyState
          icon={<PlusIcon />}
          title="No add-ons yet"
          action={{ label: "Create an add-on", href: "/settings/extras/new", primary: true }}
          renderLink={routerLink}
        >
          Add-ons are optional extras a customer can tick when booking — an extra room, a
          callout outside your usual area, materials. They add to the price and, if you want,
          to the time.
        </EmptyState>
      ) : (
        <ul className="stack">
          {state.extras.map((extra) => (
            <li key={extra.extraId}>
              <div className="row">
                <div className="stack stack--tight">
                  <div className="row__top">
                    <Link className="row__title" to={`/settings/extras/${extra.extraId}`}>
                      {extra.label}
                    </Link>
                    <StatusPill tone={extra.enabled ? "success" : "neutral"}>
                      {extra.enabled ? "Enabled" : "Disabled"}
                    </StatusPill>
                  </div>
                  {extra.description ? <span className="muted">{extra.description}</span> : null}
                  <span className="dim">
                    + {money(extra.priceAmount, extra.priceCurrency)}
                    {extra.durationMinutes > 0 ? ` · + ${duration(extra.durationMinutes)}` : ""}
                  </span>
                  <span className="dim">{appliesTo(extra)}</span>
                  <div className="cluster">
                    <Link className="btn btn-secondary" to={`/settings/extras/${extra.extraId}`}>
                      Edit
                    </Link>
                    <Button
                      variant="tertiary"
                      loading={togglingId === extra.extraId}
                      disabled={togglingId !== null}
                      onClick={() => void toggle(extra)}
                    >
                      {extra.enabled ? "Disable" : "Enable"}
                    </Button>
                    <button
                      type="button"
                      className="icon-btn"
                      aria-label={`Delete ${extra.label}`}
                      onClick={() => {
                        setActionError(null);
                        setPendingDelete(extra);
                      }}
                    >
                      <TrashIcon />
                    </button>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {pendingDelete ? (
        <ConfirmDialog
          title={`Delete "${pendingDelete.label}"?`}
          confirmLabel="Delete add-on"
          cancelLabel="Keep it"
          destructive
          confirming={deleting}
          onConfirm={() => void onDelete()}
          onCancel={closeDialog}
          body={
            <p>
              This removes the add-on for good — there's no undo. Bookings that already
              included it are not affected.
            </p>
          }
        />
      ) : null}
    </div>
  );
}
